import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import Header from "@cloudscape-design/components/header";
import Pagination from "@cloudscape-design/components/pagination";
import ProgressBar from "@cloudscape-design/components/progress-bar";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";
import TextFilter from "@cloudscape-design/components/text-filter";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { webhookBroadcastModalStore, createDefaultWebhookBroadcastFormData } from "../stores/webhookBroadcastModal";
import { webhookBroadcastStatsModalStore } from "../stores/webhookBroadcastStatsModal";
import { webhookBroadcastsStore } from "../stores/webhookBroadcasts";
import { webhookPresetModalStore, createDefaultWebhookPresetFormData } from "../stores/webhookPresetModal";
import { webhookPresetsStore } from "../stores/webhookPresets";

class WebhookBroadcasts extends React.Component {
	componentDidMount() {
		this.props.Interface.loadWebhookBroadcasts();
		this.props.Interface.loadWebhookPresets();
	}

	formatDateTime = (value) => {
		if (!value) return "-";
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return value;

		return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
	};

	getStatusType = (status) => {
		if (["completed", "success"].includes(status)) return "success";
		if (["failed", "cancelled"].includes(status)) return "error";
		if (["queued", "running"].includes(status)) return "in-progress";

		return "info";
	};

	getProgress = (item) => {
		const total = Number(item.totalUsers || 0);
		if (!total) return 0;

		return Math.min(100, Math.round((Number(item.sentCount || 0) / total) * 100));
	};

	resetBroadcastModal = () => ({
		show: true,
		loaded: true,
		formData: createDefaultWebhookBroadcastFormData(),
		audiencePreview: {
			loading: false,
			count: null,
			error: "",
		},
	});

	openPresetModal = (mode, item = null) => {
		const headers = item?.headers || {};
		const headersText = typeof headers === "string" ? headers : JSON.stringify(headers, null, 2);

		this.props.setWebhookPresetModal({
			...this.props.webhookPresetModal,
			show: true,
			mode,
			loaded: true,
			formData:
				mode === "edit" && item
					? {
							id: item.id,
							name: item.name || "",
							method: item.method || "POST",
							url: item.url || item.path || "",
							headersText: headersText || "{}",
							bodyTemplate: item.bodyTemplate || "",
							active: item.active === true || item.active === 1 || item.active === "1",
					  }
					: createDefaultWebhookPresetFormData(),
		});
	};

	openStatsModal = (item) => {
		this.props.setWebhookBroadcastStatsModal({
			...this.props.webhookBroadcastStatsModal,
			show: true,
			data: item,
		});
	};

	cancelBroadcast = async (item) => {
		try {
			await this.props.Interface.request("set", { class: "data", module: "webhookBroadcast", method: "cancel" }, { id: item.id });
			this.props.Interface.addNoty("Success", "Webhook broadcast cancelled");
			this.props.Interface.loadWebhookBroadcasts();
		} catch (error) {
			this.props.Interface.addNoty("Error", error?.text || error?.toString?.() || "Request failed");
		}
	};

	retryFailed = async (item) => {
		try {
			await this.props.Interface.request("set", { class: "data", module: "webhookBroadcast", method: "retryFailed" }, { id: item.id });
			this.props.Interface.addNoty("Success", "Failed webhook deliveries were queued again");
			this.props.Interface.loadWebhookBroadcasts();
		} catch (error) {
			this.props.Interface.addNoty("Error", error?.text || error?.toString?.() || "Request failed");
		}
	};

	deletePreset = async (item) => {
		try {
			await this.props.Interface.request("set", { class: "data", module: "webhookPreset", method: "delete" }, { id: item.id });
			this.props.Interface.addNoty("Success", "Webhook preset deleted");
			this.props.Interface.loadWebhookPresets();
		} catch (error) {
			this.props.Interface.addNoty("Error", error?.text || error?.toString?.() || "Request failed");
		}
	};

	applyBroadcastSearch = () => {
		const { webhookBroadcasts } = this.props;

		this.props.setWebhookBroadcasts({
			...webhookBroadcasts,
			pagination: { ...webhookBroadcasts.pagination, current: 1 },
		});
		setTimeout(() => this.props.Interface.loadWebhookBroadcasts(), 250);
	};

	applyPresetSearch = () => {
		const { webhookPresets } = this.props;

		this.props.setWebhookPresets({
			...webhookPresets,
			pagination: { ...webhookPresets.pagination, current: 1 },
		});
		setTimeout(() => this.props.Interface.loadWebhookPresets(), 250);
	};

	renderError = (store, onDismiss) =>
		store.loaded && store.error.text.length ? (
			<Box margin={{ bottom: "m" }}>
				<Alert type="error" header="Error loading data:" dismissible onDismiss={onDismiss}>
					<Box variant="p">{store.error.text}</Box>
					<hr />
					<Box variant="p">
						request error code: {store.error.code} | request timestamp: {store.error.timestamp}
					</Box>
				</Alert>
			</Box>
		) : null;

	renderBroadcastsTab = () => {
		const { webhookBroadcasts, config } = this.props;

		return (
			<>
				{this.renderError(webhookBroadcasts, () => this.props.setWebhookBroadcasts({ ...webhookBroadcasts, error: { text: "", code: 0, timestamp: 0 } }))}
				<Table
					stickyHeader
					trackBy="id"
					loading={!webhookBroadcasts.loaded}
					sortingColumn={{ sortingField: webhookBroadcasts.sorting.sortingColumn.column }}
					sortingDescending={webhookBroadcasts.sorting.isDescending}
					onSortingChange={(sorting) => {
						this.props.setWebhookBroadcasts({
							...webhookBroadcasts,
							sorting: {
								...webhookBroadcasts.sorting,
								sortingColumn: {
									order: !sorting.detail.isDescending ? "ASC" : "DESC",
									column: sorting.detail.sortingColumn.sortingField,
								},
								isDescending: sorting.detail.isDescending,
							},
						});
						setTimeout(() => this.props.Interface.loadWebhookBroadcasts(), 250);
					}}
					columnDefinitions={[
						{
							id: "id",
							header: "ID",
							sortingField: "id",
							cell: (item) => (
								<Button variant="normal" iconName="status-info" onClick={() => this.openStatsModal(item)}>
									{item.id}
								</Button>
							),
						},
						{ id: "name", header: "Name", sortingField: "name", cell: (item) => item.name || "-" },
						{ id: "presetName", header: "Preset", sortingField: "presetName", cell: (item) => item.presetName || item.presetId || "-" },
						{
							id: "status",
							header: "Status",
							sortingField: "status",
							cell: (item) => <StatusIndicator type={this.getStatusType(item.status)}>{item.status || "draft"}</StatusIndicator>,
						},
						{ id: "totalUsers", header: "Users", sortingField: "totalUsers", cell: (item) => item.totalUsers ?? 0 },
						{ id: "sentCount", header: "Sent", sortingField: "sentCount", cell: (item) => item.sentCount ?? 0 },
						{ id: "successCount", header: "Success", sortingField: "successCount", cell: (item) => item.successCount ?? 0 },
						{ id: "failedCount", header: "Failed", sortingField: "failedCount", cell: (item) => item.failedCount ?? 0 },
						{ id: "pendingCount", header: "Pending", sortingField: "pendingCount", cell: (item) => item.pendingCount ?? 0 },
						{
							id: "progress",
							header: "Progress",
							cell: (item) => <ProgressBar value={this.getProgress(item)} description={`${this.getProgress(item)}%`} />,
						},
						{ id: "createdAt", header: "Created", sortingField: "createdAt", cell: (item) => this.formatDateTime(item.createdAt) },
						{
							id: "actions",
							header: "Actions",
							cell: (item) => (
								<SpaceBetween direction="horizontal" size="xs">
									<Button onClick={() => this.openStatsModal(item)}>Stats</Button>
									<Button disabled={!["queued", "running"].includes(item.status)} onClick={() => this.cancelBroadcast(item)}>
										Cancel
									</Button>
								</SpaceBetween>
							),
						},
					]}
					items={webhookBroadcasts.data}
					filter={
						<SpaceBetween direction="horizontal" size="xs">
							<TextFilter
								filteringText={webhookBroadcasts.currentSearch || ""}
								filteringPlaceholder="Find broadcasts"
								onChange={({ detail }) => {
									this.props.setWebhookBroadcasts({ ...webhookBroadcasts, currentSearch: detail.filteringText });
									if (!detail.filteringText) {
										setTimeout(() => this.props.Interface.loadWebhookBroadcasts(), 250);
									}
								}}
							/>
							<Button disabled={!webhookBroadcasts.loaded} onClick={this.applyBroadcastSearch}>
								Apply
							</Button>
						</SpaceBetween>
					}
					pagination={
						<Pagination
							disabled={!webhookBroadcasts.loaded}
							currentPageIndex={webhookBroadcasts.pagination.current}
							pagesCount={webhookBroadcasts.pagination.total}
							onChange={({ detail }) => {
								this.props.setWebhookBroadcasts({
									...webhookBroadcasts,
									pagination: { ...webhookBroadcasts.pagination, current: detail.currentPageIndex },
								});
								setTimeout(() => this.props.Interface.loadWebhookBroadcasts(), 250);
							}}
						/>
					}
					preferences={
						<CollectionPreferences
							title="Preferences"
							confirmLabel="Confirm"
							cancelLabel="Cancel"
							preferences={{ pageSize: config.webhookBroadcasts.perPage }}
							pageSizePreference={{
								title: "Webhook broadcasts per page",
								options: config.webhookBroadcasts.preferences.perPage,
							}}
							onConfirm={({ detail }) => {
								localStorage.setItem("webhookBroadcasts.perPage", detail.pageSize);
								this.props.setConfig({
									...config,
									webhookBroadcasts: { ...config.webhookBroadcasts, perPage: detail.pageSize },
								});
								setTimeout(() => this.props.Interface.loadWebhookBroadcasts(), 250);
							}}
						/>
					}
					header={
						<Header
							description="Webhook broadcasts sent from the panel"
							actions={
								<SpaceBetween direction="horizontal" size="xs">
									<Button disabled={!webhookBroadcasts.loaded} onClick={() => this.props.Interface.loadWebhookBroadcasts()}>
										Refresh
									</Button>
									<Button variant="primary" iconName="add-plus" disabled={!webhookBroadcasts.loaded} onClick={() => this.props.setWebhookBroadcastModal(this.resetBroadcastModal())}>
										Create broadcast
									</Button>
								</SpaceBetween>
							}
						>
							Webhook broadcasts ({webhookBroadcasts.pagination.count})
						</Header>
					}
					empty="No webhook broadcasts found"
				/>
			</>
		);
	};

	renderPresetsTab = () => {
		const { webhookPresets, config } = this.props;

		return (
			<>
				{this.renderError(webhookPresets, () => this.props.setWebhookPresets({ ...webhookPresets, error: { text: "", code: 0, timestamp: 0 } }))}
				<Table
					stickyHeader
					trackBy="id"
					loading={!webhookPresets.loaded}
					sortingColumn={{ sortingField: webhookPresets.sorting.sortingColumn.column }}
					sortingDescending={webhookPresets.sorting.isDescending}
					onSortingChange={(sorting) => {
						this.props.setWebhookPresets({
							...webhookPresets,
							sorting: {
								...webhookPresets.sorting,
								sortingColumn: {
									order: !sorting.detail.isDescending ? "ASC" : "DESC",
									column: sorting.detail.sortingColumn.sortingField,
								},
								isDescending: sorting.detail.isDescending,
							},
						});
						setTimeout(() => this.props.Interface.loadWebhookPresets(), 250);
					}}
					columnDefinitions={[
						{
							id: "id",
							header: "ID",
							sortingField: "id",
							cell: (item) => (
								<Button variant="normal" iconName="edit" onClick={() => this.openPresetModal("edit", item)}>
									{item.id}
								</Button>
							),
						},
						{ id: "name", header: "Name", sortingField: "name", cell: (item) => item.name || "-" },
						{ id: "method", header: "Method", sortingField: "method", cell: (item) => item.method || "-" },
						{ id: "url", header: "URL / path", sortingField: "url", cell: (item) => item.url || item.path || "-" },
						{
							id: "active",
							header: "Active",
							sortingField: "active",
							cell: (item) => <StatusIndicator type={item.active === true || item.active === 1 || item.active === "1" ? "success" : "stopped"}>{item.active === true || item.active === 1 || item.active === "1" ? "active" : "inactive"}</StatusIndicator>,
						},
						{ id: "createdAt", header: "Created", sortingField: "createdAt", cell: (item) => this.formatDateTime(item.createdAt) },
						{
							id: "actions",
							header: "Actions",
							cell: (item) => (
								<SpaceBetween direction="horizontal" size="xs">
									<Button onClick={() => this.openPresetModal("edit", item)}>Edit</Button>
									<Button onClick={() => this.deletePreset(item)}>Delete</Button>
								</SpaceBetween>
							),
						},
					]}
					items={webhookPresets.data}
					filter={
						<SpaceBetween direction="horizontal" size="xs">
							<TextFilter
								filteringText={webhookPresets.currentSearch || ""}
								filteringPlaceholder="Find presets"
								onChange={({ detail }) => {
									this.props.setWebhookPresets({ ...webhookPresets, currentSearch: detail.filteringText });
									if (!detail.filteringText) {
										setTimeout(() => this.props.Interface.loadWebhookPresets(), 250);
									}
								}}
							/>
							<Button disabled={!webhookPresets.loaded} onClick={this.applyPresetSearch}>
								Apply
							</Button>
						</SpaceBetween>
					}
					pagination={
						<Pagination
							disabled={!webhookPresets.loaded}
							currentPageIndex={webhookPresets.pagination.current}
							pagesCount={webhookPresets.pagination.total}
							onChange={({ detail }) => {
								this.props.setWebhookPresets({
									...webhookPresets,
									pagination: { ...webhookPresets.pagination, current: detail.currentPageIndex },
								});
								setTimeout(() => this.props.Interface.loadWebhookPresets(), 250);
							}}
						/>
					}
					preferences={
						<CollectionPreferences
							title="Preferences"
							confirmLabel="Confirm"
							cancelLabel="Cancel"
							preferences={{ pageSize: config.webhookPresets.perPage }}
							pageSizePreference={{
								title: "Webhook presets per page",
								options: config.webhookPresets.preferences.perPage,
							}}
							onConfirm={({ detail }) => {
								localStorage.setItem("webhookPresets.perPage", detail.pageSize);
								this.props.setConfig({
									...config,
									webhookPresets: { ...config.webhookPresets, perPage: detail.pageSize },
								});
								setTimeout(() => this.props.Interface.loadWebhookPresets(), 250);
							}}
						/>
					}
					header={
						<Header
							description="Webhook endpoint presets and request templates"
							actions={
								<SpaceBetween direction="horizontal" size="xs">
									<Button disabled={!webhookPresets.loaded} onClick={() => this.props.Interface.loadWebhookPresets()}>
										Refresh
									</Button>
									<Button variant="primary" iconName="add-plus" disabled={!webhookPresets.loaded} onClick={() => this.openPresetModal("add")}>
										Create preset
									</Button>
								</SpaceBetween>
							}
						>
							Webhook presets ({webhookPresets.pagination.count})
						</Header>
					}
					empty="No webhook presets found"
				/>
			</>
		);
	};

	render() {
		return (
			<Tabs
				tabs={[
					{ id: "broadcasts", label: "Broadcasts", content: this.renderBroadcastsTab() },
					{ id: "presets", label: "Presets", content: this.renderPresetsTab() },
				]}
			/>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const webhookBroadcasts = useRecoilValue(webhookBroadcastsStore);
		const setWebhookBroadcasts = useSetRecoilState(webhookBroadcastsStore);
		const webhookPresets = useRecoilValue(webhookPresetsStore);
		const setWebhookPresets = useSetRecoilState(webhookPresetsStore);
		const webhookBroadcastModal = useRecoilValue(webhookBroadcastModalStore);
		const setWebhookBroadcastModal = useSetRecoilState(webhookBroadcastModalStore);
		const webhookPresetModal = useRecoilValue(webhookPresetModalStore);
		const setWebhookPresetModal = useSetRecoilState(webhookPresetModalStore);
		const webhookBroadcastStatsModal = useRecoilValue(webhookBroadcastStatsModalStore);
		const setWebhookBroadcastStatsModal = useSetRecoilState(webhookBroadcastStatsModalStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);

		return (
			<Component
				{...props}
				webhookBroadcasts={webhookBroadcasts}
				setWebhookBroadcasts={setWebhookBroadcasts}
				webhookPresets={webhookPresets}
				setWebhookPresets={setWebhookPresets}
				webhookBroadcastModal={webhookBroadcastModal}
				setWebhookBroadcastModal={setWebhookBroadcastModal}
				webhookPresetModal={webhookPresetModal}
				setWebhookPresetModal={setWebhookPresetModal}
				webhookBroadcastStatsModal={webhookBroadcastStatsModal}
				setWebhookBroadcastStatsModal={setWebhookBroadcastStatsModal}
				config={config}
				setConfig={setConfig}
			/>
		);
	};
}

export default withRecoil(WebhookBroadcasts);
